// src/pages/TicketDetailPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { fmtDateTime, timeAgo, statusClass, priorityLabel, ticketCode, copyToClipboard } from '../utils/formatters';
import { getUser } from '../utils/auth';
import { API_BASE } from '../utils/api';
import {
  ArrowLeft, MessageSquare, Paperclip, Send, Tag, Eye,
  X, Zap, BookOpen, Copy, AlertTriangle, ChevronRight, Loader, Plus, Search,
  CheckCircle2, PlayCircle, PauseCircle, RotateCcw, UserCheck, Clock, RefreshCw, Sparkles, Wand2
} from 'lucide-react';

/* ── constants ─────────────────────────────────────── */
const C = {
  nav: 'var(--text-60a5fa)',
  border: 'var(--white-05)',
  bg: 'transparent',
  ptxt: 'var(--text-primary)',
  stxt: 'var(--text-muted)',
  card: 'var(--ticket-card)',
};

const isImage = (filename) => /\.(jpg|jpeg|png|gif|webp)$/i.test(filename || '');
const isPDF = (filename) => /\.pdf$/i.test(filename || '');
const isText = (filename) => /\.(txt|log|json|md|csv)$/i.test(filename || '');
const getFileExt = (filename) => (filename || '').split('.').pop().toUpperCase();

const handleForceDownload = async (url, filename, e) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  try {
    const token = localStorage.getItem('beo_token') || sessionStorage.getItem('beo_token');
    const res = await fetch(url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    if (!res.ok) throw new Error('Failed to download file');
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('Download error:', err);
    window.open(url, '_blank');
  }
};

const TextFilePreview = ({ url }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('beo_token') || sessionStorage.getItem('beo_token');
    fetch(url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.text();
      })
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load text file preview:', err);
        setError(true);
        setLoading(false);
      });
  }, [url]);

  if (loading) return <div style={{ fontSize: 13, color: C.stxt, padding: 12 }}>Loading preview...</div>;
  if (error) return <div style={{ fontSize: 13, color: '#DC2626', padding: 12 }}>Preview unavailable</div>;

  return (
    <div style={{
      width: '100%', flex: 1, overflowY: 'auto', background: 'var(--black-40)', color: 'var(--text-primary)',
      padding: '16px', borderRadius: '12px', fontSize: '13px', fontFamily: 'monospace', whiteSpace: 'pre-wrap',
      margin: 0, border: '1px solid var(--white-06)'
    }}>
      {content}
    </div>
  );
};

const AttachmentPreviewModal = ({ attachment, onClose, backendUrl }) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    if (!attachment) return;
    let active = true;
    let createdUrl = null;
    const token = localStorage.getItem('beo_token') || sessionStorage.getItem('beo_token');
    const downloadUrl = `${backendUrl}/api/attachments/${attachment.id}/download`;

    fetch(downloadUrl, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch preview');
        return res.blob();
      })
      .then(blob => {
        if (!active) return;
        const ext = getFileExt(attachment.file_name || attachment.file_path).toLowerCase();
        const mimeMap = {
          jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
          gif: 'image/gif', webp: 'image/webp', pdf: 'application/pdf', txt: 'text/plain'
        };
        const finalBlob = mimeMap[ext] ? new Blob([blob], { type: mimeMap[ext] }) : blob;
        createdUrl = window.URL.createObjectURL(finalBlob);
        setBlobUrl(createdUrl);
      })
      .catch(() => {
        if (active) setBlobUrl(`${backendUrl}/api/uploads/${attachment.file_path}`);
      });

    return () => {
      active = false;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
      setBlobUrl(null);
    };
  }, [attachment, backendUrl]);

  if (!attachment) return null;

  const targetName = attachment.file_name || attachment.file_path || '';
  const fileUrl = blobUrl || `${backendUrl}/api/uploads/${attachment.file_path}`;
  const isImg = isImage(targetName);
  const isPdf = isPDF(targetName);
  const isTxt = isText(targetName);

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 9999,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--dark-modal)', borderRadius: '20px', width: '100%', maxWidth: '80%',
          maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 25px 50px -12px var(--black-50)',
          overflow: 'hidden', border: '1px solid var(--white-06)', backdropFilter: 'blur(20px)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid var(--text-e2e8f0)', background: 'var(--black-30)' }}>
          <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Paperclip size={16} color="var(--text-64748b)" />
            {attachment.file_name} <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>({getFileExt(attachment.file_name)})</span>
          </div>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', borderRadius: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '24px', background: 'rgba(96, 165, 250, 0.05)', display: 'flex', justifyContent: 'center' }}>
          {isImg ? (
            <img src={fileUrl} alt={attachment.file_name} style={{ maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }} />
          ) : isPdf ? (
            <iframe src={fileUrl} title={attachment.file_name} style={{ width: '100%', height: 'calc(80vh - 120px)', border: 'none', borderRadius: '8px', background: 'var(--surface)' }} />
          ) : isTxt ? (
            <TextFilePreview url={`${backendUrl}/api/attachments/${attachment.id}/download`} />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface)', borderRadius: '8px', width: '100%' }}>
              <div style={{ marginBottom: '16px' }}><Eye size={48} strokeWidth={1} color="#CBD5E1" /></div>
              <p style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 500, color: 'var(--text-primary)' }}>Preview not available.</p>
              <p style={{ margin: 0, fontSize: '14px' }}>Please download the file to view its contents.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── tiny helpers ─────────────────────────────────── */
const Avatar = ({ name = '?', size = 32, style = {} }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: 'rgba(96, 165, 250, 0.2)', color: 'var(--text-60a5fa)',
    border: '1px solid rgba(96, 165, 250, 0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.38, fontWeight: 700, ...style,
  }}>
    {(name || '?').charAt(0).toUpperCase()}
  </div>
);

const MetaRow = ({ label, value }) => value ? (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.stxt, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, color: C.ptxt }}>{value}</div>
  </div>
) : null;

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: C.stxt, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>{children}</div>
);


/* ── status action map ────────────────────────────── */
const STATUS_ACTIONS = {
  'Open': [{ label: 'Start Progress', next: 'In Progress', icon: <PlayCircle size={14} /> }],
  'In Progress': [
    { label: 'Move to On Hold', next: 'On Hold', icon: <PauseCircle size={14} /> },
    { label: 'Mark as Resolved', next: 'Resolved', icon: <CheckCircle2 size={14} />, needsNote: true },
  ],
  'On Hold': [{ label: 'Resume Work', next: 'In Progress', icon: <PlayCircle size={14} /> }],
  'Resolved': [
    { label: 'Reopen', next: 'Reopened', icon: <RotateCcw size={14} /> },
    { label: 'Close Ticket', next: 'Closed', icon: <CheckCircle2 size={14} />, needsClosureNote: true },
  ],
  'Closed': [{ label: 'Reopen', next: 'Reopened', icon: <RotateCcw size={14} /> }],
};

/* ── status & priority badge helpers ────────────────── */
const StatusBadge = ({ status, neutralTheme = false }) => {
  const styles = neutralTheme ? {
    'Open': { bg: 'var(--black-30)', color: 'var(--text-secondary)', border: 'var(--white-06)' },
    'In Progress': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-primary)', border: 'rgba(96, 165, 250, 0.2)' },
    'On Hold': { bg: 'var(--black-30)', color: 'var(--text-secondary)', border: 'var(--white-06)' },
    'Resolved': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-primary)', border: 'rgba(96, 165, 250, 0.2)' },
    'Closed': { bg: 'var(--black-30)', color: 'var(--text-secondary)', border: 'var(--white-06)' },
    'Reopened': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-primary)', border: 'rgba(96, 165, 250, 0.2)' },
  } : {
    'Open': { bg: 'rgba(30, 64, 175, 0.1)', color: 'var(--text-60a5fa)', border: 'rgba(96, 165, 250, 0.2)' },
    'In Progress': { bg: 'rgba(194, 65, 12, 0.1)', color: '#FB923C', border: 'rgba(251, 146, 60, 0.2)' },
    'On Hold': { bg: 'rgba(180, 83, 9, 0.1)', color: 'var(--text-fbbf24)', border: 'rgba(251, 191, 36, 0.2)' },
    'Resolved': { bg: 'rgba(4, 120, 87, 0.1)', color: 'var(--text-34d399)', border: 'rgba(52, 211, 153, 0.2)' },
    'Closed': { bg: 'var(--white-05)', color: 'var(--text-muted)', border: 'var(--white-06)' },
    'Reopened': { bg: 'rgba(185, 28, 28, 0.1)', color: '#F87171', border: 'rgba(248, 113, 113, 0.2)' },
  };
  const s = styles[status] || styles['Open'];
  return (
    <span style={{
      padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.04em', background: s.bg, color: s.color,
      border: `1px solid ${s.border}`
    }}>
      {status}
    </span>
  );
};

const PriorityBadge = ({ priority, neutralTheme = false }) => {
  const p = (priority || 'low').toLowerCase();
  const styles = neutralTheme ? {
    critical: { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-primary)', border: 'rgba(96, 165, 250, 0.2)' },
    high: { bg: 'var(--black-30)', color: 'var(--text-secondary)', border: 'var(--white-06)' },
    medium: { bg: 'var(--black-30)', color: 'var(--text-secondary)', border: 'var(--white-06)' },
    low: { bg: 'transparent', color: 'var(--text-muted)', border: 'var(--white-06)' },
  } : {
    critical: { bg: 'rgba(185, 28, 28, 0.15)', color: '#F87171', border: 'rgba(248, 113, 113, 0.3)' },
    high: { bg: 'rgba(194, 65, 12, 0.15)', color: '#FB923C', border: 'rgba(251, 146, 60, 0.3)' },
    medium: { bg: 'rgba(30, 64, 175, 0.15)', color: 'var(--text-60a5fa)', border: 'rgba(96, 165, 250, 0.3)' },
    low: { bg: 'var(--white-05)', color: 'var(--text-muted)', border: 'var(--white-06)' },
  };
  const s = styles[p] || styles.low;
  return (
    <span style={{
      padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.04em', background: s.bg, color: s.color,
      border: `1px solid ${s.border}`
    }}>
      {p}
    </span>
  );
};

/* ─────────────────────────────────────────────────── */
export default function TicketDetailPage({ showToast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, loading, backendUrl } = useApi();
  const user = getUser();
  const isIT = ['admin', 'it'].includes(user?.role);

  /* state */
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [latestActivity, setLatestActivity] = useState(null);
  const [assignees, setAssignees] = useState([]);

  const [newComment, setNewComment] = useState('');
  const [attachFiles, setAttachFiles] = useState([]);
  const [isInternal, setIsInternal] = useState(false);
  const [notifyCc, setNotifyCc] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [rephraseLoading, setRephraseLoading] = useState(false);
  const [rephraseResult, setRephraseResult] = useState("");
  const [lastRephraseOutput, setLastRephraseOutput] = useState("");

  const [pendingStatus, setPendingStatus] = useState(null);
  const [resNote, setResNote] = useState('');

  const [newTag, setNewTag] = useState('');
  const [tags, setTags] = useState([]);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [ccSearch, setCcSearch] = useState('');
  const [ccResults, setCcResults] = useState([]);
  const [ccLoading, setCcLoading] = useState(false);
  const [isUpdatingCc, setIsUpdatingCc] = useState(false);

  const commentBoxRef = useRef(null);

  /* Clear rephrase suggestion when textarea is cleared */
  useEffect(() => {
    if (!newComment || newComment.trim() === "") {
      setRephraseResult("");
    }
  }, [newComment]);

  /* load ─────────────────────────────────────────── */
  const load = useCallback(async () => {
    try {
      const [tData, cData, hData, aData] = await Promise.all([
        request(`/api/tickets/${id}`),
        request(`/api/comments/${id}`),
        request(`/api/tickets/${id}/history`),
        isIT ? request('/api/users/assignable') : Promise.resolve([]),
      ]);
      setTicket(tData);
      setComments(cData);
      setHistory(hData);

      // Requirement 4: Frontend filtering for assignable users
      const validAssignees = (aData || []).filter(u => {
        if (!u) return false;
        if (u.role !== 'admin') return false;
        if (u.is_deleted) return false;
        if (u.is_active === false) return false;
        if (u.is_internal) return false;
        const uname = (u.username || u.name || '').toLowerCase();
        if (uname === 'admin' || uname === 'system' || uname === 'root' || uname === 'recovery' || uname === 'breakglass' || uname.includes('protected')) return false;
        const uemail = (u.email || '').toLowerCase();
        if (uemail.startsWith('admin@') || uemail.startsWith('system@') || uemail.startsWith('root@') || uemail.startsWith('administrator@') || uemail.includes('recovery') || uemail.includes('breakglass')) return false;
        const uid = String(u.id || '').toLowerCase();
        if (uid === 'admin' || uid === 'system' || uid === 'root' || uid === 'administrator' || uid === '1') return false;
        return true;
      });
      setAssignees(validAssignees);

      setTags(tData.tags || []);

      // Determine latest activity for compact preview
      const allEvents = [
        ...cData.map(c => ({ ts: c.created_at, label: `${c.author_username} added a comment`, sub: c.body?.substring(0, 60) })),
        ...hData.map(h => {
          const d = h.details || {};
          let label = `${h.actor_username || 'System'} updated ticket`;
          if (h.event_type === 'created') label = `${h.actor_username || 'System'} created ticket`;
          else if (d.status) label = `${h.actor_username || 'System'} changed status → ${d.status.to}`;
          else if (h.event_type === 'assigned') label = `${h.actor_username || 'System'} assigned ticket`;
          else if (h.event_type === 'reopened') label = `${h.actor_username || 'System'} reopened ticket`;
          return { ts: h.created_at, label, sub: '' };
        }),
      ].sort((a, b) => new Date(b.ts) - new Date(a.ts));

      setLatestActivity(allEvents[0] || null);
    } catch (err) {
      showToast(err.message, 'error');
      navigate('/my-queue');
    }
  }, [id, request, isIT, navigate, showToast]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await load();
    setTimeout(() => setIsRefreshing(false), 800);
  };

  useEffect(() => { load(); }, [load]);

  /* SSE live comments */
  useEffect(() => {
    const token = localStorage.getItem('beo_token') || sessionStorage.getItem('beo_token');
    if (!token) return;
    const source = new EventSource(`${API_BASE}/api/comments/${id}/stream?token=${encodeURIComponent(token)}`);
    source.onmessage = (event) => {
      try {
        const incoming = JSON.parse(event.data);
        setComments(prev => {
          if (prev.some(c => c.id === incoming.id)) return prev;
          const optIdx = prev.findIndex(c => c.id?.toString().startsWith('temp-') && c.body === incoming.body);
          if (optIdx !== -1) {
            const updated = [...prev];
            updated[optIdx] = incoming;
            return updated;
          }
          return [...prev, incoming];
        });
      } catch { }
    };
    source.onerror = () => source.close();
    return () => source.close();
  }, [id]);

  /* actions ──────────────────────────────────────── */
  const handleStatusAction = async (nextStatus, needsNote = false, needsClosure = false) => {
    if ((needsNote || needsClosure) && !pendingStatus) {
      setPendingStatus(nextStatus);
      return;
    }

    const trimmed = resNote.trim();
    if (needsNote) {
      if (!trimmed) {
        showToast('Resolution note is required.', 'error');
        return;
      }
      if (trimmed.length < 10) {
        showToast('Resolution note must be at least 10 characters.', 'error');
        return;
      }
      if (trimmed.length > 2000) {
        showToast('Resolution note exceeds 2000 characters.', 'error');
        return;
      }
    }

    if (needsClosure) {
      if (!trimmed) {
        showToast('Closure note is required.', 'error');
        return;
      }
      if (trimmed.length < 10) {
        showToast('Closure note must be at least 10 characters.', 'error');
        return;
      }
      if (trimmed.length > 500) {
        showToast('Closure note exceeds 500 characters.', 'error');
        return;
      }
    }

    try {
      const body = { status: nextStatus };
      if (needsNote) body.resolution_note = trimmed;
      if (needsClosure) body.closure_note = trimmed;

      await request(`/api/tickets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      showToast(`Status → ${nextStatus}`, 'success');
      setPendingStatus(null);
      setResNote('');
      load();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleAssign = async (uid) => {
    try {
      await request(`/api/tickets/${id}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({ assigned_to: uid }),
      });
      showToast('Assignee updated', 'success');
      load();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handlePriority = async (priority) => {
    try {
      await request(`/api/tickets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ priority }),
      });
      showToast(`Priority → ${priority}`, 'success');
      load();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleAssignToMe = () => handleAssign(user.id);

  const handleEscalate = async () => {
    try {
      await request(`/api/tickets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ priority: 'critical' }),
      });
      showToast('Ticket escalated to Critical', 'success');
      load();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleClone = async () => {
    try {
      const t = ticket;
      const newTicket = await request('/api/tickets', {
        method: 'POST',
        body: JSON.stringify({
          title: `[Clone] ${t.title}`,
          description: t.description,
          priority: t.priority,
          category: t.category,
          ticket_type: t.ticket_type,
          os: t.os,
          affected_system: t.affected_system,
        }),
      });
      showToast('Ticket cloned successfully', 'success');
      navigate(`/tickets/${newTicket.id}`);
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    const body = newComment.trim();
    const optimisticId = `temp-${Date.now()}`;
    setComments(prev => [...prev, {
      id: optimisticId, body, author_username: user.username,
      created_at: new Date().toISOString(), is_internal: isInternal, is_pending: true,
    }]);
    setNewComment('');
    const savedInternal = isInternal;
    setIsInternal(false);

    try {
      const fd = new FormData();
      fd.append('body', body);
      // Ensure correctly typed boolean or omitted if false to avoid truthy string "false"
      if (savedInternal) {
        fd.append('is_internal', 'true');
      }
      if (notifyCc) {
        fd.append('notify_cc', 'true');
      }
      attachFiles.forEach(f => fd.append('attachments', f));
      setAttachFiles([]);
      const token = localStorage.getItem('beo_token') || sessionStorage.getItem('beo_token');
      const res = await fetch(`${API_BASE}/api/comments/${id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setComments(prev => {
        if (prev.some(c => c.id === data.id)) return prev.filter(c => c.id !== optimisticId);
        return prev.map(c => c.id === optimisticId ? data : c);
      });
      showToast('Comment added', 'success');
    } catch (err) {
      setComments(prev => prev.filter(c => c.id !== optimisticId));
      showToast(err.message, 'error');
      setNewComment(body);
    }
  };

  const handleAiSuggest = async () => {
    if (!ticket) return;
    setAiLoading(true);
    try {
      const data = await request('/api/ai/assist', {
        method: 'POST',
        body: JSON.stringify({ title: ticket.title, description: ticket.description }),
      });
      const suggestion = data.suggested_fix?.join('\n') || data.problem_summary || 'No suggestion available.';
      setNewComment(`AI Suggestion:\n${suggestion}`);
      commentBoxRef.current?.focus();
    } catch (err) {
      showToast('AI assist unavailable', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateKB = () => {
    navigate(`/knowledge-base?createForTicket=${id}`);
  };

  const handleRephrase = async () => {
    const textarea = document.getElementById("comment-input");
    const currentText = textarea ? textarea.value : (newComment || "");
    const cleanText = currentText.replace(/\s+/g, " ").trim();


    if (!cleanText || cleanText.length < 3) {
      return;
    }

    if (rephraseLoading) return;
    setRephraseLoading(true);
    try {
      const data = await request('/api/rephrase', {
        method: 'POST',
        body: JSON.stringify({
          text: cleanText,
          variationId: Date.now()
        }),
      });
      const output = data?.rephrased || cleanText;
      setLastRephraseOutput(output);
      setRephraseResult(output);
    } catch (err) {
      console.error("Rephrase failed:", err);
      setRephraseResult(cleanText);
    } finally {
      setRephraseLoading(false);
    }
  };

  const handleRefreshRephrase = () => {
    handleRephrase();
  };

  const handleUseRephrase = () => {
    setNewComment(rephraseResult);
    setRephraseResult("");
    commentBoxRef.current?.focus();
  };

  const handleCcSearch = async (val) => {
    setCcSearch(val);
    if (val.length < 2) {
      setCcResults([]);
      return;
    }
    setCcLoading(true);
    try {
      const results = await request(`/api/users/search?q=${encodeURIComponent(val)}`);
      const validResults = (results || []).filter(u => {
        if (!u) return false;
        if (u.is_internal) return false;
        if (u.is_active === false) return false;
        if (u.is_deleted) return false;
        const uname = (u.username || u.name || '').toLowerCase();
        if (uname === 'admin' || uname === 'system' || uname === 'root' || uname === 'recovery' || uname === 'breakglass' || uname.includes('protected')) return false;
        const uemail = (u.email || '').toLowerCase();
        if (uemail.startsWith('admin@') || uemail.startsWith('system@') || uemail.startsWith('root@') || uemail.startsWith('administrator@') || uemail.includes('recovery') || uemail.includes('breakglass')) return false;
        const uid = String(u.id || '').toLowerCase();
        if (uid === 'admin' || uid === 'system' || uid === 'root' || uid === 'administrator' || uid === '1') return false;
        return true;
      });
      setCcResults(validResults);
    } catch (err) {
      console.error('CC Search failed:', err);
    } finally {
      setCcLoading(false);
    }
  };

  const updateCcUsers = async (newCcList) => {
    setIsUpdatingCc(true);
    try {
      await request(`/api/tickets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ cc_users: newCcList }),
      });
      setTicket(prev => ({ ...prev, watchers: newCcList }));
    } catch (err) {
      showToast('Failed to update watchers', 'error');
    } finally {
      setIsUpdatingCc(false);
    }
  };

  const handleAddCc = (u) => {
    const current = ticket.watchers || [];
    if (current.some(x => x.email.toLowerCase() === u.email.toLowerCase())) {
      showToast('User already in CC', 'info');
      return;
    }
    const newList = [...current, { email: u.email, username: u.username, id: u.id }];
    updateCcUsers(newList);
    setCcSearch('');
    setCcResults([]);
  };

  const handleRemoveCc = (email) => {
    const newList = (ticket.watchers || []).filter(x => x.email.toLowerCase() !== email.toLowerCase());
    updateCcUsers(newList);
  };

  const handleAddTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && newTag.trim()) {
      e.preventDefault();
      const tag = newTag.trim().replace(/,/g, '');
      if (!tags.includes(tag)) setTags(prev => [...prev, tag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (t) => setTags(prev => prev.filter(x => x !== t));

  /* history event label */
  const historyLabel = (h) => {
    const d = h.details || {};
    if (h.event_type === 'created') return 'created this ticket';
    if (h.event_type === 'reopened') return `reopened ticket (was ${d.status?.from || 'Closed'})`;
    if (h.event_type === 'assigned') return `assigned ticket`;
    if (h.event_type === 'automation') return `automation rule applied: ${d.rule_name || ''}`;
    if (d.status) return `changed status → ${d.status.to}`;
    if (d.priority) return `changed priority → ${d.priority.to}`;
    return `updated ticket`;
  };

  /* ── render ──────────────────────────────────────── */
  if (!ticket && loading) return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <div className="spinner" />
    </div>
  );
  if (!ticket) return null;

  const code = ticketCode(ticket);
  const actions = STATUS_ACTIONS[ticket.status] || [];
  const isCreator = ticket.created_by === user?.id;
  const isWatcherOnly = !isIT && !isCreator;

  return (
    <div style={{ background: C.bg, minHeight: 'calc(100vh - 80px)', padding: '24px 32px' }}>
      <style>{`
        .tdw-card { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 16px; padding: 24px; margin-bottom: 20px; backdrop-filter: blur(16px); }
        .tdw-sidebar { position: sticky; top: 16px; align-self: start; display: flex; flex-direction: column; gap: 16px; }
        .tdw-sidebar-card { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 16px; padding: 20px; backdrop-filter: blur(16px); }
        .tdw-action-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid var(--white-06); background: var(--white-05); color: ${C.ptxt}; transition: all 0.2s; }
        .tdw-action-btn:hover { background: var(--white-06); border-color: var(--white-20); }
        .tdw-action-btn.primary { background: var(--text-60a5fa); color: #fff; border: none; }
        .tdw-action-btn.primary:hover { opacity: 0.9; transform: translateY(-1px); }
        .tdw-workflow-btn { display: flex; align-items: center; gap: 8px; width: 100%; padding: 10px 14px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid var(--white-05); background: var(--white-03); color: ${C.ptxt}; margin-bottom: 8px; transition: all 0.2s; }
        .tdw-workflow-btn:hover { background: rgba(96, 165, 250, 0.1); border-color: rgba(96, 165, 250, 0.3); color: var(--text-60a5fa); }
        .tdw-select { width: 100%; height: 44px; padding: 0 12px; border-radius: 10px; border: 1px solid var(--white-06); outline: none; background: transparent; font-size: 14px; cursor: pointer; color: ${C.ptxt}; transition: all 0.2s; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2300F0FF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; transform: translate(0, 0) !important; }
        .tdw-select:focus { border-color: var(--text-60a5fa); background: var(--surface-3); box-shadow: 0 0 12px rgba(96, 165, 250, 0.2); }
        .tdw-select option { background: var(--surface); color: var(--text-primary); }
        .tdw-timeline-item { display: flex; gap: 12px; padding: 14px 0; border-bottom: 1px solid var(--white-04); }
        .tdw-timeline-item:last-child { border-bottom: none; }
        .tdw-comment-internal { background: rgba(245, 158, 11, 0.05); border-left: 3px solid #F59E0B; padding-left: 12px; border-radius: 0 12px 12px 0; }
        .tdw-tag-pill { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; background: var(--white-05); border: 1px solid var(--white-06); border-radius: 20px; font-size: 12px; font-weight: 600; color: ${C.ptxt}; }
        .tdw-tag-pill button { background: none; border: none; cursor: pointer; color: ${C.stxt}; display: flex; line-height: 1; padding: 0; }
        .tdw-tag-pill button:hover { color: #F87171; }
        .tdw-watcher-avatar { position: relative; }
        .tdw-watcher-avatar button { position: absolute; top: -4px; right: -4px; width: 14px; height: 14px; border-radius: 50%; background: #EF4444; color: #fff; border: none; cursor: pointer; font-size: 9px; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.15s; }
        .tdw-watcher-avatar:hover button { opacity: 1; }
        .tdw-comment-tabs { display: flex; gap: 8px; margin-bottom: 12px; }
        .tdw-tab-btn { padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; border: 1px solid var(--white-06); background: var(--white-03); color: ${C.stxt}; transition: all 0.2s; }
        .tdw-tab-btn.active { background: var(--text-60a5fa); color: #fff; border-color: var(--text-60a5fa); }
        .tdw-attach-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: var(--white-03); border-radius: 10px; border: 1px solid var(--white-05); margin-bottom: 6px; font-size: 13px; }
        .rephrase-box { background: rgba(139, 92, 246, 0.05); border: 1px solid rgba(139, 92, 246, 0.2); border-radius: 12px; padding: 16px; margin-bottom: 16px; }
        .rephrase-box .label { font-size: 11px; font-weight: 800; color: #A78BFA; text-transform: uppercase; margin: 0 0 8px; letter-spacing: 0.05em; }
        .rephrase-box .content { font-size: 14px; color: var(--text-primary); line-height: 1.6; margin: 0 0 12px; white-space: pre-wrap; }
        .rephrase-box .actions { display: flex; gap: 8px; }
        .tdw-activity-row { display: flex; gap: 12px; padding: 14px 0; border-bottom: 1px solid var(--white-04); font-size: 13px; color: ${C.ptxt}; align-items: flex-start; }
        .tdw-activity-row:last-child { border-bottom: none; }
        .tdw-activity-icon { width: 28px; height: 28px; border-radius: 50%; background: var(--white-03); border: 1px solid var(--white-05); display: flex; alignItems: center; justifyContent: center; flexShrink: 0; margin-top: 2px; }
        .tdw-date-divider { display: flex; align-items: center; gap: 12px; margin: 24px 0 16px; }
        .tdw-date-divider span { font-size: 11px; font-weight: 800; color: ${C.stxt}; text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap; }
        .tdw-date-divider::before, .tdw-date-divider::after { content: ''; flex: 1; height: 1px; background: var(--white-05); }
        .comment-item-hover:hover { transform: translateY(-1px); border-color: var(--text-60a5fa)77 !important; }
        .tdw-description-text { color: var(--text-94a3b8) !important; }
        .tdw-title-text { color: var(--text-94a3b8) !important; }
      `}</style>


      {/* ── Polished Admin Header ── */}
      <div style={{ background: 'var(--ticket-card)', borderRadius: '20px', border: '1px solid var(--white-05)', padding: '24px 32px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', backdropFilter: 'blur(16px)', boxShadow: '0 20px 25px -5px var(--black-30)' }}>
        <div style={{ flex: 1 }}>
          {isIT ? (
            <>
              <button
                onClick={() => navigate(-1)}
                style={{ background: 'none', border: 'none', padding: 0, margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.textDecoration = 'none'; }}
              >
                &larr; Back to Tickets
              </button>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <div
                  onClick={() => { copyToClipboard(code); showToast('Copied ID', 'success'); }}
                  style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-60a5fa)', background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.2)', padding: '4px 12px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                  title="Click to copy"
                >
                  #{code} <Copy size={12} />
                </div>
                <StatusBadge status={ticket.status} />
                <PriorityBadge priority={ticket.priority} />
                <div style={{ height: 16, width: 1, background: 'var(--white-06)' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.stxt, fontWeight: 500 }}>
                  <UserCheck size={14} /> Assigned: <span style={{ color: C.ptxt, fontWeight: 600 }}>{ticket.assigned_to_username || 'Unassigned'}</span>
                </div>
              </div>

              <h1 className="tdw-title-text" style={{ margin: '0 0 12px', fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                {ticket.title}
              </h1>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar name={ticket.created_by_username || '?'} size={24} />
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Created by <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{ticket.created_by_username || 'User'}</span>
                    <span style={{ margin: '0 6px', color: 'var(--white-06)' }}>|</span>
                    {ticket.creator_email || 'No email provided'}
                  </div>
                </div>
                <div style={{ height: 4, width: 4, borderRadius: '50%', background: 'rgba(96, 165, 250, 0.2)' }} />
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {timeAgo(ticket.created_at)}
                </div>
                <div style={{ height: 4, width: 4, borderRadius: '50%', background: 'rgba(96, 165, 250, 0.2)' }} />
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Updated {timeAgo(ticket.updated_at)}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Employee SaaS Header */}
              <button
                onClick={() => navigate(-1)}
                style={{ background: 'none', border: 'none', padding: 0, margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.textDecoration = 'none'; }}
              >
                &larr; Back to Tickets
              </button>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <div
                  onClick={() => { copyToClipboard(code); showToast('Copied ID', 'success'); }}
                  style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-60a5fa)', background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.2)', padding: '4px 12px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                  title="Click to copy"
                >
                  #{code} <Copy size={12} />
                </div>
                <div style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: '6px' }}><StatusBadge status={ticket.status} neutralTheme={true} /></div>
                <div style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: '6px' }}><PriorityBadge priority={ticket.priority} neutralTheme={true} /></div>
              </div>

              <h1 className="tdw-title-text" style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                {ticket.title}
              </h1>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.stxt, flexWrap: 'wrap' }}>
                <span style={{ color: C.ptxt, fontWeight: 600 }}>Created by: {ticket.created_by === user.id ? 'You' : (ticket.created_by_username || 'User')}</span>
                <span style={{ color: 'rgba(96, 165, 250, 0.2)' }}>•</span>
                <span>{timeAgo(ticket.created_at)}</span>
                <span style={{ color: 'rgba(96, 165, 250, 0.2)' }}>•</span>
                <span>Updated {timeAgo(ticket.updated_at)}</span>
                <span style={{ color: 'rgba(96, 165, 250, 0.2)' }}>•</span>
                <span className="blink-green" style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-3b82f6)', fontWeight: 600 }}>
                  <PlayCircle size={12} /> Updated just now
                </span>
                <style>{`
                  @keyframes blink-green {
                    0% { opacity: 1; }
                    50% { opacity: 0.4; }
                    100% { opacity: 1; }
                  }
                  .blink-green {
                    animation: blink-green 3s ease-in-out infinite;
                  }
                  @keyframes spin-refresh { 100% { transform: rotate(360deg); } }
                  .spin-refresh { animation: spin-refresh 0.8s linear; }
                `}</style>
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginLeft: 20 }}>
          <button className="tdw-action-btn primary" onClick={() => navigate(`/tickets/${id}/track`, { replace: true })} style={{ height: 38 }}>
            Track Ticket
          </button>
          <button className="tdw-action-btn" onClick={handleRefresh} style={{ height: 38, width: 38, padding: 0, justifyContent: 'center' }} title="Refresh ticket data">
            <RefreshCw size={16} className={isRefreshing ? 'spin-refresh' : ''} />
          </button>
        </div>
      </div>

      {/* ── 70 / 30 grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, alignItems: 'start' }}>

        {/* ───── LEFT CONTENT ───── */}
        <div>
          {/* Compact Info Bar */}
          {!isIT && (
            <div className="tdw-card" style={{ padding: '16px 24px', marginBottom: '20px' }}>
              {/* Status Progress Indicator */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                {['Open', 'In Progress', 'Resolved', 'Closed'].map((s, idx) => {
                  const isActive = ticket.status === s || (ticket.status === 'Reopened' && s === 'Open') || (ticket.status === 'On Hold' && s === 'In Progress');
                  const isPast = ['Open', 'In Progress', 'Resolved', 'Closed'].indexOf(ticket.status === 'Reopened' ? 'Open' : (ticket.status === 'On Hold' ? 'In Progress' : ticket.status)) >= idx;
                  return (
                    <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: isActive ? 1 : (isPast ? 0.7 : 0.4) }}>
                        <div style={{ width: 16, height: 16, borderRadius: '50%', background: isActive ? 'var(--text-primary)' : (isPast ? 'var(--text-muted)' : 'var(--white-06)'), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isPast && !isActive && <CheckCircle2 size={10} color="#fff" />}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: isActive ? 700 : 600, color: isActive ? C.ptxt : C.stxt }}>{s}</span>
                      </div>
                      {idx < 3 && <div style={{ height: 2, flex: 1, background: isPast ? 'var(--text-muted)' : 'var(--white-06)', opacity: isPast ? 0.5 : 1, margin: '0 8px' }} />}
                    </div>
                  );
                })}
              </div>
              <div style={{ height: 1, background: 'rgba(96, 165, 250, 0.05)', marginBottom: 16 }} />

              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <UserCheck size={14} color="var(--text-64748b)" />
                  <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Assigned:</span> {ticket.assigned_to_username || 'Unassigned'}
                </span>
                <span style={{ color: 'rgba(96, 165, 250, 0.2)' }}>|</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Zap size={14} color="var(--text-64748b)" />
                  <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Priority:</span> {priorityLabel(ticket.priority) || 'Low'}
                </span>
                <span style={{ color: 'rgba(96, 165, 250, 0.2)' }}>|</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <BookOpen size={14} color="var(--text-64748b)" />
                  <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Category:</span> {ticket.category || 'Support'}
                </span>
                <span style={{ color: 'rgba(96, 165, 250, 0.2)' }}>|</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Clock size={14} color="var(--text-64748b)" />
                  <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Updated:</span> {timeAgo(ticket.updated_at)}
                </span>
              </div>
            </div>
          )}

          {/* Description Section */}
          <div className="tdw-card">
            <SectionLabel>Description</SectionLabel>
            <div style={{ background: 'var(--black-20)', padding: '16px 20px', borderRadius: '16px', border: '1px solid var(--white-05)' }}>
              <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                <span className="tdw-description-text" style={{ color: 'var(--text-94a3b8)' }}>
                  {ticket.description || <em style={{ color: C.stxt }}>No description provided.</em>}
                </span>
              </div>
            </div>

            {ticket.resolution_note && (
              <div style={{ marginTop: 20, padding: '16px 20px', background: 'var(--black-20)', borderRadius: '12px', border: '1px solid var(--white-06)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.05em' }}>Resolution Note</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>{ticket.resolution_note}</div>
              </div>
            )}

            {ticket.closure_note && (
              <div style={{ marginTop: 12, padding: '16px 20px', background: 'var(--black-20)', borderRadius: '12px', border: '1px solid var(--white-06)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.05em' }}>Closure Note</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>{ticket.closure_note}</div>
              </div>
            )}
          </div>

          {/* Ticket-level attachments */}
          {ticket.attachments?.length > 0 && (
            <div className="tdw-card">
              <SectionLabel>Attachments ({ticket.attachments.length})</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {ticket.attachments.map(a => (
                  <div key={a.id}>
                    <div className="tdw-attach-item" style={{ marginBottom: 0 }}>
                      <Paperclip size={14} style={{ color: C.stxt, flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.file_name} <span style={{ fontSize: 10, color: C.stxt, fontWeight: 700 }}>({getFileExt(a.file_name)})</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setPreviewAttachment(a)}
                        style={{ fontSize: 12, fontWeight: 600, color: C.nav, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        View
                      </button>
                      <span style={{ color: C.border, margin: '0 8px' }}>|</span>
                      <a
                        href={`${API_BASE}/api/attachments/${a.id}/download`}
                        onClick={(e) => handleForceDownload(`${API_BASE}/api/attachments/${a.id}/download`, a.file_name, e)}
                        download={a.file_name}
                        style={{ fontSize: 12, fontWeight: 600, color: C.nav, textDecoration: 'none', cursor: 'pointer' }}
                      >
                        Download
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity & Comments */}
          <div className="tdw-card">
            {/* Header row: label + latest activity preview + Track button */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.stxt, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Comments</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {isIT && latestActivity && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.stxt, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Clock size={12} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {latestActivity.label} · {timeAgo(latestActivity.ts)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced Comments List */}
            {comments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: isIT ? '32px 0' : '16px 0 12px', color: C.stxt, fontSize: 13, fontStyle: 'italic' }}>
                {isIT ? 'No communication history yet.' : 'No updates yet. Start the conversation to get help.'}
              </div>
            ) : (
              <div style={{ marginBottom: 24, maxHeight: 500, overflowY: 'auto', paddingRight: 8 }}>
                {(() => {
                  const sorted = [...comments].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                  const groups = [];
                  let lastDate = '';

                  sorted.forEach(c => {
                    const dateStr = new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    const todayStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    const yesterdayStr = new Date(Date.now() - 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

                    let displayDate = dateStr;
                    if (dateStr === todayStr) displayDate = 'Today';
                    else if (dateStr === yesterdayStr) displayDate = 'Yesterday';

                    if (displayDate !== lastDate) {
                      groups.push({ type: 'divider', date: displayDate });
                      lastDate = displayDate;
                    }
                    groups.push({ type: 'comment', data: c });
                  });

                  return groups.map((item, idx) => {
                    if (item.type === 'divider') {
                      return <div key={`div-${idx}`} className="tdw-date-divider"><span>{item.date}</span></div>;
                    }
                    const c = item.data;
                    return (
                      <div key={c.id} style={{
                        display: 'flex', gap: 14, padding: '16px', borderRadius: 16,
                        background: c.is_internal ? 'rgba(245, 158, 11, 0.05)' : 'var(--white-02)',
                        border: c.is_internal ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid var(--white-05)',
                        marginBottom: 12, transition: 'all 0.2s'
                      }}>
                        <Avatar name={c.author_username} size={36} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 700, fontSize: 13, color: C.ptxt }}>{c.author_username}</span>
                              {c.is_internal && <span style={{ fontSize: 10, fontWeight: 700, background: '#F59E0B', color: 'var(--surface)', padding: '2px 6px', borderRadius: 4 }}>INTERNAL NOTE</span>}
                            </div>
                            <span style={{ fontSize: 12, color: C.stxt }}>{timeAgo(c.created_at)}</span>
                          </div>
                          <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {c.body}
                          </div>
                          {c.attachments?.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {c.attachments.map(a => (
                                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'rgba(96, 165, 250, 0.05)', border: '1px solid var(--text-e2e8f0)', borderRadius: 6, fontSize: 11, color: C.nav }}>
                                    <Paperclip size={12} />
                                    <span style={{ marginRight: 4 }}>{a.file_name} <span style={{ fontSize: 9, opacity: 0.7 }}>({getFileExt(a.file_name)})</span></span>
                                    <button
                                      type="button"
                                      onClick={() => setPreviewAttachment(a)}
                                      style={{ fontSize: 11, fontWeight: 600, color: C.nav, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                    >
                                      View
                                    </button>
                                    <span style={{ color: 'rgba(96, 165, 250, 0.2)', margin: '0 2px' }}>|</span>
                                    <a
                                      href={`${API_BASE}/api/attachments/${a.id}/download`}
                                      onClick={(e) => handleForceDownload(`${API_BASE}/api/attachments/${a.id}/download`, a.file_name, e)}
                                      download={a.file_name}
                                      style={{ fontWeight: 600, color: C.nav, textDecoration: 'none', cursor: 'pointer' }}
                                    >
                                      Download
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* CC Watchers Management (Admin Only) - Refined Premium UI */}
            {user?.role === 'admin' && (
              <div style={{ marginBottom: 24, padding: '0 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>CC Watchers</div>
                  <div style={{ height: 1, flex: 1, background: 'var(--white-05)' }} />
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  {(ticket.watchers || []).map(w => (
                    <div
                      key={w.email}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                        background: 'rgba(96, 165, 250, 0.04)', border: '1px solid rgba(96, 165, 250, 0.15)',
                        borderRadius: '8px', transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)'; e.currentTarget.style.background = 'rgba(96, 165, 250, 0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.15)'; e.currentTarget.style.background = 'rgba(96, 165, 250, 0.04)'; }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-60a5fa)' }}>{w.username || w.email.split('@')[0]}</span>
                      <button
                        onClick={() => handleRemoveCc(w.email)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(96, 165, 250, 0.6)', transition: 'color 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.color = '#F87171'}
                        onMouseLeave={e => e.currentTarget.style.color = 'rgba(96, 165, 250, 0.6)'}
                      >
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  ))}
                  {(!ticket.watchers || ticket.watchers.length === 0) && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', opacity: 0.7 }}>No participants added to CC.</span>
                  )}
                </div>

                <div style={{ position: 'relative', maxWidth: 320 }}>
                  <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', opacity: 0.5 }}>
                    {ccLoading ? <Loader size={13} className="spin" /> : <Search size={13} />}
                  </div>
                  <input
                    type="text"
                    placeholder="Add participant..."
                    value={ccSearch}
                    onChange={e => handleCcSearch(e.target.value)}
                    style={{
                      width: '100%', height: 34, background: 'var(--white-03)', border: '1px solid var(--white-06)',
                      borderRadius: '8px', padding: '0 12px 0 34px', fontSize: 12, outline: 'none',
                      color: 'var(--text-primary)', transition: 'all 0.2s'
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = 'var(--text-60a5fa)'}
                    onBlur={e => e.currentTarget.style.borderColor = 'var(--white-06)'}
                  />
                  {ccResults.length > 0 && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 100,
                      background: 'var(--dark-modal)', border: '1px solid var(--white-10)', borderRadius: '10px',
                      maxHeight: 220, overflowY: 'auto', boxShadow: '0 12px 24px -6px rgba(0,0,0,0.6)', backdropFilter: 'blur(16px)'
                    }}>
                      {ccResults.map(u => (
                        <div
                          key={u.email}
                          onClick={() => handleAddCc(u)}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--white-05)', transition: 'background 0.2s' }}
                          onMouseOver={e => e.currentTarget.style.background = 'var(--white-05)'}
                          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{u.username}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Comment Composer */}
            {!isWatcherOnly && (
              <form onSubmit={handleAddComment}>
              {/* toggle row: Reply / Internal + Notify CC */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {isIT && (
                    <div className="tdw-comment-tabs" style={{ marginBottom: 0 }}>
                      <button type="button" className={`tdw-tab-btn ${!isInternal ? 'active' : ''}`} onClick={() => setIsInternal(false)}>Reply</button>
                      <button type="button" className={`tdw-tab-btn ${isInternal ? 'active' : ''}`} onClick={() => setIsInternal(true)}>Internal</button>
                    </div>
                  )}

                  {/* Notify CC Toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                    <div
                      onClick={() => setNotifyCc(!notifyCc)}
                      style={{
                        width: 32, height: 18, borderRadius: 9, background: notifyCc ? 'var(--text-60a5fa)' : 'var(--white-10)',
                        position: 'relative', transition: 'all 0.2s', border: '1px solid var(--white-06)'
                      }}
                    >
                      <div style={{
                        width: 12, height: 12, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 2, left: notifyCc ? 17 : 2, transition: 'all 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: notifyCc ? 'var(--text-primary)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notify CC</span>
                  </label>
                </div>
              </div>

              <textarea
                id="comment-input"
                ref={commentBoxRef}
                placeholder={isIT ? (isInternal ? 'Internal note… (Ctrl + Enter to send)' : 'Write a reply… (Ctrl + Enter to send)') : 'Write your reply… (Ctrl + Enter to send)'}
                value={newComment}
                onChange={e => {
                  setNewComment(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && newComment.trim()) {
                    handleAddComment(e);
                  }
                }}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '14px 16px', borderRadius: 12,
                  border: `1px solid ${isInternal ? 'rgba(245, 158, 11, 0.3)' : C.border}`,
                  background: isInternal ? 'rgba(245, 158, 11, 0.05)' : 'var(--black-30)',
                  color: C.ptxt,
                  fontSize: 14, fontFamily: 'inherit', resize: 'none', outline: 'none', marginBottom: 12,
                  minHeight: 100, transition: 'all 0.2s', boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              />

              {rephraseResult && (
                <div className="rephrase-box">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Sparkles size={14} style={{ color: '#8B5CF6' }} />
                    <p className="label" style={{ marginBottom: 0 }}>AI Suggestion</p>
                  </div>
                  <p className="content">{rephraseResult}</p>
                  <div className="actions">
                    <button type="button" className="tdw-action-btn primary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={handleUseRephrase}>Use Suggestion</button>
                    <button type="button" className="tdw-action-btn" style={{ fontSize: 12, padding: '6px 12px' }} onClick={handleRefreshRephrase}>Refresh</button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13, color: C.ptxt, background: 'var(--white-10)', padding: '8px 16px', borderRadius: '10px', border: '1px solid var(--white-06)', fontWeight: 600, transition: 'all 0.2s' }}>
                    <Paperclip size={15} />
                    <span>Attach</span>
                    <input type="file" multiple onChange={e => setAttachFiles(Array.from(e.target.files))} style={{ display: 'none' }} />
                    {attachFiles.length > 0 && <span style={{ color: 'var(--text-60a5fa)', fontWeight: 600 }}>({attachFiles.length})</span>}
                  </label>

                  <button type="button" className="tdw-action-btn" style={{ fontSize: 12, padding: '5px 10px' }} onClick={handleRephrase} disabled={rephraseLoading}>
                    {rephraseLoading ? <Loader size={12} className="spin" /> : <Wand2 size={16} />}
                    {rephraseLoading ? 'Rephrasing...' : 'Rephrase'}
                  </button>
                </div>

                <button type="submit" disabled={!newComment.trim() || loading} className="tdw-action-btn primary" style={{ gap: 6, minWidth: 100, justifyContent: 'center', padding: '8px 16px' }} title="Send reply (Ctrl + Enter)">
                  {loading ? <Loader size={13} className="spin" /> : <><Send size={13} /> Send Reply</>}
                </button>
              </div>
              </form>
            )}
            {isWatcherOnly && (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, background: 'var(--black-20)', borderRadius: 12, border: '1px dashed var(--white-10)' }}>
                You have read-only participant access to this ticket.
              </div>
            )}
          </div>

          {/* Activity Timeline – Admin only inline */}
          {isIT && user?.role !== 'admin' && history.length > 0 && (
            <div className="tdw-card">
              <SectionLabel>Activity History</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {[...history]
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map(h => (
                    <div key={h.id} className="tdw-activity-row">
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, marginTop: 2,
                      }}>
                        {h.event_type === 'assigned' ? <UserCheck size={13} style={{ color: 'var(--brand-dark)' }} /> :
                          h.event_type === 'created' ? <Plus size={13} style={{ color: '#059669' }} /> :
                            <RefreshCw size={13} style={{ color: 'var(--text-muted)' }} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                          {h.actor_username || 'System'}&nbsp;
                          <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>{historyLabel(h)}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {fmtDateTime(h.created_at)} &middot; {timeAgo(h.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* ───── RIGHT SIDEBAR ───── */}
        <div className="tdw-sidebar">
          {/* Employee Context Section */}
          {!isIT && (
            <div className="tdw-sidebar-card">
              <SectionLabel>Context</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--black-20)', width: 28, height: 28, borderRadius: 8, border: '1px solid var(--white-05)' }}>
                    <BookOpen size={14} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Category</div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{ticket.category || '—'}</div>
                  </div>
                </div>

                <div style={{ height: 1, background: 'rgba(96, 165, 250, 0.05)' }} />

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--black-20)', width: 28, height: 28, borderRadius: 8, border: '1px solid var(--white-05)' }}>
                    <Zap size={14} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>OS / System</div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{ticket.os || '—'}</div>
                  </div>
                </div>

                <div style={{ height: 1, background: 'rgba(96, 165, 250, 0.05)' }} />

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--black-20)', width: 28, height: 28, borderRadius: 8, border: '1px solid var(--white-05)' }}>
                    <Tag size={14} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Type</div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{ticket.ticket_type || '—'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Action Buttons (IF Employee has specific actions, like Close/Reopen) */}
          {!isIT && !isWatcherOnly && ['Resolved', 'Closed'].includes(ticket.status) && (
            <div className="tdw-sidebar-card">
              <SectionLabel>Actions</SectionLabel>
              {ticket.status === 'Resolved' && (
                <>
                  <button className="tdw-workflow-btn" onClick={() => handleStatusAction('Reopened')}>
                    <RotateCcw size={14} /> Reopen Ticket
                  </button>
                  <button className="tdw-workflow-btn" onClick={() => handleStatusAction('Closed')}>
                    <CheckCircle2 size={14} /> Close Ticket
                  </button>
                </>
              )}
              {ticket.status === 'Closed' && (
                <button className="tdw-workflow-btn" onClick={() => handleStatusAction('Reopened')}>
                  <RotateCcw size={14} /> Reopen Ticket
                </button>
              )}
            </div>
          )}

          {/* Workflow Actions */}
          {isIT && actions.length > 0 && (
            <div className="tdw-sidebar-card">
              <SectionLabel>Workflow</SectionLabel>
              {/* Workflow Actions with Note handling */}
              {actions.map(a => (
                <button key={a.next} className="tdw-workflow-btn" onClick={() => handleStatusAction(a.next, a.needsNote, a.needsClosureNote)}>
                  {a.icon} {a.label}
                </button>
              ))}

              {/* Resolution / Closure note input */}
              {pendingStatus && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.ptxt, marginBottom: 6 }}>
                    {pendingStatus === 'Closed' ? 'Closure Note Required' : 'Resolution Note Required'}
                  </div>
                  <textarea
                    value={resNote}
                    onChange={e => setResNote(e.target.value)}
                    maxLength={pendingStatus === 'Closed' ? 500 : 2000}
                    placeholder={pendingStatus === 'Closed' ? "Describe the reason for closure (mandatory)…" : "Describe the technical resolution (mandatory)…"}
                    rows={4}
                    style={{ 
                      width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 13, 
                      fontFamily: 'inherit', borderRadius: 10, border: `1px solid ${resNote.trim().length > 0 && resNote.trim().length < 10 ? '#EF4444' : C.border}`, 
                      background: 'var(--black-30)', color: C.ptxt, outline: 'none', resize: 'vertical', 
                      marginBottom: 4 
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: resNote.trim().length > 0 && resNote.trim().length < 10 ? '#F87171' : C.stxt }}>
                      {resNote.trim().length < 10 && resNote.trim().length > 0 && "Min. 10 chars required"}
                    </div>
                    <div style={{ fontSize: 11, color: C.stxt, textAlign: 'right' }}>
                      {resNote.length} / {pendingStatus === 'Closed' ? 500 : 2000}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="tdw-action-btn primary" style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                      onClick={() => handleStatusAction(pendingStatus, pendingStatus === 'Resolved', pendingStatus === 'Closed')}>
                      Confirm
                    </button>
                    <button className="tdw-action-btn" style={{ fontSize: 12 }} onClick={() => { setPendingStatus(null); setResNote(''); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Status + Priority selects (IT ONLY) */}
          {isIT && (
            <div className="tdw-sidebar-card">
              <SectionLabel>Manage Status</SectionLabel>
              <select 
                className="tdw-select" 
                value={pendingStatus || ticket.status} 
                onChange={e => {
                  const nextStatus = e.target.value;
                  handleStatusAction(nextStatus, nextStatus === 'Resolved', nextStatus === 'Closed');
                }} 
                style={{ marginBottom: 16 }}
              >
                {['Open', 'In Progress', 'On Hold', 'Resolved', 'Closed'].map(s => (
                  <option key={s}>{s}</option>
                ))}
              </select>

              <SectionLabel>Assignee</SectionLabel>
              <select className="tdw-select" value={ticket.assigned_to || ''} onChange={e => handleAssign(e.target.value)} style={{ marginBottom: 16 }}>
                <option value="" disabled>Select assignee</option>
                {assignees.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
              </select>

              <SectionLabel>Priority</SectionLabel>
              <select className="tdw-select" value={ticket.priority || 'low'} onChange={e => handlePriority(e.target.value)} style={{ marginBottom: 16 }}>
                {['low', 'medium', 'high', 'critical'].map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>

              <SectionLabel>Tags</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {tags.map(t => (
                  <span key={t} className="tdw-tag-pill">
                    {t}
                    <button onClick={() => handleRemoveTag(t)}><X size={10} /></button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                placeholder="Add tag…"
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                onKeyDown={handleAddTag}
                style={{ width: '100%', height: 38, background: 'var(--black-20)', border: '1px dashed var(--white-20)', borderRadius: 10, padding: '0 12px', fontSize: 13, outline: 'none', color: C.ptxt, transition: 'all 0.2s' }}
              />
            </div>
          )}

        </div>
      </div>

      {/* ── Employee Activity Modal ── */}
      {!isIT && isActivityModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--black-70)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setIsActivityModalOpen(false)}>
          <div style={{ background: 'var(--dark-modal)', borderRadius: '24px', border: '1px solid var(--white-06)', width: '100%', maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px var(--black-50)', backdropFilter: 'blur(20px)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px 32px', borderBottom: '1px solid var(--white-05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>Ticket Activity</h2>
              <button
                onClick={() => setIsActivityModalOpen(false)}
                style={{ background: 'var(--white-05)', border: '1px solid var(--white-06)', borderRadius: '10px', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--white-06)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'var(--white-05)'; }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '32px 32px 48px', overflowY: 'auto', flex: 1, scrollBehavior: 'smooth' }}>
              {history.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, fontStyle: 'italic', padding: '40px 0' }}>No activity recorded yet.</div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* Vertical Timeline Guide */}
                  <div style={{ position: 'absolute', top: 16, bottom: 0, left: 17, width: 2, background: 'rgba(96, 165, 250, 0.05)', zIndex: 0 }} />

                  {[...history]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((h, index, arr) => {
                      const d = h.details || {};
                      let IconComponent = MessageSquare;
                      if (h.event_type === 'created') IconComponent = Plus;
                      else if (h.event_type === 'assigned') IconComponent = UserCheck;
                      else if (h.event_type === 'reopened') IconComponent = RotateCcw;
                      else if (d.status) IconComponent = RefreshCw;

                      return (
                        <div key={h.id} style={{ position: 'relative', display: 'flex', gap: 24, paddingBottom: index === arr.length - 1 ? 0 : 28, zIndex: 1 }}>
                          {/* Timeline Node */}
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'rgba(96, 165, 250, 0.1)', border: '2px solid rgba(96, 165, 250, 0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, color: 'var(--text-60a5fa)', position: 'relative', zIndex: 2,
                            boxShadow: '0 0 15px rgba(96, 165, 250, 0.1)'
                          }}>
                            <IconComponent size={14} strokeWidth={2.5} />
                          </div>

                          {/* Activity Card */}
                          <div
                            style={{
                              flex: 1,
                              background: 'var(--surface)',
                              border: '1px solid var(--text-e2e8f0)',
                              borderRadius: '12px',
                              padding: '14px 18px',
                              transition: 'background 0.2s',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96, 165, 250, 0.05)'; e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.2)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'var(--white-02)'; e.currentTarget.style.borderColor = 'var(--white-05)'; }}
                          >
                            <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                              <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{h.actor_username || 'System'}</span>
                              {' '}
                              <span style={{ fontWeight: 500 }}>{historyLabel(h)}</span>
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Clock size={12} /> {timeAgo(h.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          backendUrl={backendUrl}
        />
      )}
    </div>
  );
}
